import { z } from 'zod';

import { PersonLabelDto } from './person.js';

/**
 * Every object type an audit event can be written against.
 *
 * The array is the source of truth and the type is derived from it, so a subset
 * cannot be expressed. It lives in `shared` because three packages need it: the
 * API's `?objectType=` Zod enum, the admin console's filter dropdown, and the
 * `AuditObjectType` used by the repositories. Two hand-maintained copies had
 * already drifted — both omitted `admin_category`, `user` and `team_membership`,
 * so the API returned 400 and the console offered no option for events that had
 * been written for months.
 */
export const AUDIT_OBJECT_TYPES = [
  'timesheet',
  'timesheet_line',
  'time_entry',
  'project',
  'task',
  'assignment',
  'line_classification',
  'admin_category',
  'task_team_membership',
  'user',
  'team',
  'team_membership',
  // General activities grants (a category assigned by leads). One per grant
  // table, as task_team_membership and team_membership are: a team's "whole
  // team" setting (objectId = team id, category in the payload) and a person's
  // individual choice (objectId = the person's user id, category in the
  // payload). Keyed on the team or person rather than the category so the audit
  // console's object column answers "whose access changed?".
  'activity_team_default',
  'activity_person_override',
  'reporting_period',
  'system',
] as const;

export type AuditObjectType = (typeof AUDIT_OBJECT_TYPES)[number];

export const AuditEventDto = z.object({
  eventId: z.string().uuid(),
  createdAt: z.string(),
  eventType: z.string(),
  objectType: z.string(),
  objectId: z.string().nullable(),
  objectName: z.string().nullable(),
  // audit_events.actor_user_id is NOT NULL with an FK to users, so every event has an
  // actor and it always resolves to a user row.
  actorUserId: z.string(),
  // The actor's name and avatar initials. Initials are computed by the API from the AD
  // name parts (api/src/repositories/person.ts); the web renders them verbatim and must
  // never derive initials itself. Automated jobs write as the seeded System user, an
  // ordinary user row, so they arrive as { displayName: 'System', initials: 'S' }.
  actor: PersonLabelDto,
  actorRole: z.string().nullable(),
  beforeJson: z.unknown().nullable(),
  afterJson: z.unknown().nullable(),
  detail: z.string().nullable(),
  reason: z.string().nullable(),
  correlationId: z.string().nullable(),
});
export type AuditEventDto = z.infer<typeof AuditEventDto>;

export const AuditLogResponse = z.object({
  total: z.number().int(),
  events: z.array(AuditEventDto),
});
export type AuditLogResponse = z.infer<typeof AuditLogResponse>;
